import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle, MessageSquare, Send, Loader, Eye, EyeOff } from 'lucide-react';
import './SendingSettings.css';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassModal } from '../ui/GlassModal';
import { smsApi, SmsConfig, SmsConfigUpsert } from '../../services/api';
import SmsOptOutsPanel from './SmsOptOutsPanel';

/** Normalize FastAPI error detail — may be a string, an object, or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
    const detail = e?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
    }
    if (typeof detail === 'object') {
        if (detail.code === 'sms_opted_out') return `This number (${detail.phone}) has opted out and cannot be sent to.`;
        if (detail.code === 'sms_throttled') return 'Send throttled — please wait a moment and try again.';
        if (detail.code === 'trial_restriction') return 'Sending SMS requires a paid plan. Please upgrade to continue.';
        return JSON.stringify(detail);
    }
    return fallback;
};

// BLOCKER-003 / IMPR-008 / BLOCKER-005: a client-side estimate of GSM-7 vs
// UCS-2 segmentation, so a user can see BEFORE sending that a long/emoji-laden
// message will split into multiple segments (and that the opt-out footer,
// appended server-side, can push it over a boundary). Mirrors
// mobilemessage_service.compute_segments() (the authoritative, server-side
// computation) unit-for-unit: GSM-7 extension-table characters cost 2
// septets, and this MUST NOT under-count relative to the server — an
// under-estimate here gives a false sense of safety, an over-estimate is
// merely a slightly pessimistic UI. Plain `text.length` was the bug: it's
// already correct for UCS-2 (JS string length IS UTF-16 code units, so a
// surrogate-pair emoji already counts as 2), but it silently counted GSM-7
// extension characters (`^{}[~]|€`) as 1 septet instead of 2.
const GSM7_BASIC = "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ\x1bÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM7_EXT = "^{}\\[~]|€";
const GSM7_BASIC_SET = new Set(Array.from(GSM7_BASIC));
const GSM7_EXT_SET = new Set(Array.from(GSM7_EXT));
const GSM7_SET = new Set([...Array.from(GSM7_BASIC_SET), ...Array.from(GSM7_EXT_SET)]);

function estimateSmsSegments(text: string): { length: number; segments: number; encoding: 'GSM-7' | 'UCS-2' } {
    const chars = Array.from(text); // code-point-correct iteration (handles surrogate pairs as one entry)
    const isUnicode = chars.some(ch => !GSM7_SET.has(ch));
    const singleLimit = isUnicode ? 70 : 160;
    const multiLimit = isUnicode ? 67 : 153;
    // UCS-2 budget is denominated in UTF-16 code units — `text.length` already
    // is that (a non-BMP char is a surrogate pair, i.e. 2 units). GSM-7 budget
    // is denominated in septets — extension-table characters cost 2, not 1.
    const units = isUnicode
        ? text.length
        : chars.reduce((sum, ch) => sum + (GSM7_EXT_SET.has(ch) ? 2 : 1), 0);
    const segments = units === 0 ? 1 : units <= singleLimit ? 1 : Math.ceil(units / multiLimit);
    return { length: units, segments, encoding: isUnicode ? 'UCS-2' : 'GSM-7' };
}

const EMPTY_CONFIG: SmsConfig = {
    exists: false, provider: 'twilio', account_ref: null, has_credential: false,
    credential_hint: null, sender_id: null, from_number: null,
    verified: false, is_active: true, source: 'env',
};

export default function SmsSendingSettings() {
    const [config, setConfig] = useState<SmsConfig>(EMPTY_CONFIG);
    const [form, setForm] = useState<SmsConfigUpsert>({ provider: 'twilio', is_active: true });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [showSecret, setShowSecret] = useState(false);
    const [testTo, setTestTo] = useState('');
    const [testBody, setTestBody] = useState('AIME test message');
    const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);
    const [confirmTestOpen, setConfirmTestOpen] = useState(false);
    const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

    useEffect(() => {
        loadConfig();
    }, []);

    const loadConfig = async () => {
        setLoading(true);
        try {
            const data = await smsApi.getConfig();
            setConfig(data);
            setForm({
                provider: data.provider, account_ref: data.account_ref || undefined,
                sender_id: data.sender_id || undefined, from_number: data.from_number || undefined,
                is_active: data.is_active,
            });
        } catch (e: any) {
            setError(getApiError(e, 'Failed to load SMS configuration'));
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setSaved(false);
        try {
            const data = await smsApi.saveConfig(form);
            setConfig(data);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (e: any) {
            setError(getApiError(e, 'Failed to save configuration'));
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        setConfirmTestOpen(false);
        setTesting(true);
        setTestResult(null);
        try {
            const res = await smsApi.testSend({ to: testTo, body: testBody });
            setTestResult({ success: true, message: `Sent via ${res.provider} (status: ${res.status})${res.stub ? ' — stub mode, no real SMS sent' : ''}` });
        } catch (e: any) {
            setTestResult({ success: false, message: getApiError(e, 'Test send failed') });
        } finally {
            setTesting(false);
        }
    };

    const handleRemove = async () => {
        setConfirmRemoveOpen(false);
        try {
            await smsApi.deleteConfig();
            setConfig(EMPTY_CONFIG);
            setForm({ provider: 'twilio', is_active: true });
        } catch (e: any) {
            setError(getApiError(e, 'Failed to remove configuration'));
        }
    };

    if (loading) return (
        <div className="sending-settings">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)' }}>
                <Loader size={16} className="spin" /> Loading…
            </div>
        </div>
    );

    return (
        <div className="sending-settings">
            <div className="sending-header">
                <MessageSquare size={20} style={{ color: 'var(--primary)' }} />
                <div>
                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>SMS Sending</h2>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                        Connect a Twilio or mobilemessage.com.au account to send outbound SMS from AIME.
                    </p>
                </div>
            </div>

            {config.verified && (
                <div className="sending-verified-banner">
                    <CheckCircle size={16} />
                    <span>Verified — sending via <strong>{config.provider}</strong></span>
                </div>
            )}

            {!config.verified && config.exists && (
                <div className="sending-warn-banner">
                    <AlertTriangle size={16} />
                    <span>SMS configured but not yet verified. Send a test SMS to confirm it works.</span>
                </div>
            )}

            {!config.exists && (
                <div className="sending-warn-banner">
                    <AlertTriangle size={16} />
                    <span>
                        No SMS provider configured for your account
                        {config.source === 'env' ? ' — falling back to the platform default account, if configured' : ' — sends will use the stub/dev fallback (no real SMS sent)'}.
                    </span>
                </div>
            )}

            <GlassCard>
                <div className="sending-form">
                    <div className="sending-row">
                        <div className="sending-field">
                            <label className="sending-label">Provider</label>
                            <select
                                className="sending-input"
                                value={form.provider}
                                onChange={e => setForm(f => ({ ...f, provider: e.target.value }))}
                            >
                                <option value="twilio">Twilio</option>
                                <option value="mobilemessage">mobilemessage.com.au</option>
                            </select>
                        </div>
                    </div>

                    <div className="sending-row">
                        <div className="sending-field">
                            <label className="sending-label">
                                {form.provider === 'mobilemessage' ? 'Username' : 'Account SID'}
                            </label>
                            <input
                                className="sending-input"
                                type="text"
                                value={form.account_ref || ''}
                                placeholder={form.provider === 'mobilemessage' ? 'you@example.com' : 'AC…'}
                                onChange={e => setForm(f => ({ ...f, account_ref: e.target.value }))}
                            />
                        </div>
                        <div className="sending-field" style={{ position: 'relative' }}>
                            <label className="sending-label">
                                {form.provider === 'mobilemessage' ? 'Password' : 'Auth Token'}
                            </label>
                            <input
                                className="sending-input"
                                type={showSecret ? 'text' : 'password'}
                                value={form.credential || ''}
                                placeholder={config.has_credential ? (config.credential_hint || '••••••••') : ''}
                                onChange={e => setForm(f => ({ ...f, credential: e.target.value }))}
                            />
                            <button
                                type="button"
                                aria-label={showSecret ? 'Hide' : 'Show'}
                                onClick={() => setShowSecret(s => !s)}
                                style={{ position: 'absolute', right: 8, top: 30, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
                            >
                                {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                            </button>
                        </div>
                    </div>

                    {form.provider === 'mobilemessage' ? (
                        <div className="sending-row">
                            <div className="sending-field">
                                <label className="sending-label">Sender ID</label>
                                <input
                                    className="sending-input"
                                    type="text"
                                    value={form.sender_id || ''}
                                    placeholder="YOURBRAND"
                                    onChange={e => setForm(f => ({ ...f, sender_id: e.target.value }))}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="sending-row">
                            <div className="sending-field">
                                <label className="sending-label">From Number (E.164)</label>
                                <input
                                    className="sending-input"
                                    type="text"
                                    value={form.from_number || ''}
                                    placeholder="+61400000000 (leave blank to use the platform default)"
                                    onChange={e => setForm(f => ({ ...f, from_number: e.target.value }))}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </GlassCard>

            {error && (
                <div className="sending-error">
                    <AlertTriangle size={14} /> {error}
                </div>
            )}

            {testResult && (
                <div className={testResult.success ? 'sending-verified-banner' : 'sending-error'}>
                    {testResult.success ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
                    {testResult.message}
                </div>
            )}

            <div className="sending-actions">
                <GlassButton variant="primary" onClick={handleSave} disabled={saving}>
                    {saving ? <Loader size={14} className="spin" /> : null}
                    {saved ? 'Saved!' : saving ? 'Saving…' : 'Save'}
                </GlassButton>

                <GlassButton
                    variant="secondary"
                    onClick={() => setConfirmTestOpen(true)}
                    disabled={testing || !testTo}
                >
                    {testing ? <Loader size={14} className="spin" /> : <Send size={14} />}
                    {testing ? 'Sending…' : 'Send Test SMS'}
                </GlassButton>

                {config.exists && (
                    <GlassButton variant="danger" onClick={() => setConfirmRemoveOpen(true)}>
                        Remove
                    </GlassButton>
                )}
            </div>

            <GlassCard>
                <div className="sending-form">
                    <h4 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Test send</h4>
                    <div className="sending-row">
                        <div className="sending-field">
                            <label className="sending-label">To (phone number)</label>
                            <input
                                className="sending-input"
                                type="text"
                                value={testTo}
                                placeholder="0412 345 678"
                                onChange={e => setTestTo(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="sending-field">
                        <label className="sending-label">Message</label>
                        <input
                            className="sending-input"
                            type="text"
                            value={testBody}
                            onChange={e => setTestBody(e.target.value)}
                        />
                        {(() => {
                            const counter = estimateSmsSegments(testBody);
                            return (
                                <span style={{ display: 'block', marginTop: 4, fontSize: 11, color: 'var(--text-secondary)' }}>
                                    {counter.length} characters · {counter.segments} segment{counter.segments !== 1 ? 's' : ''} ({counter.encoding})
                                    {' — an opt-out footer may be appended before sending and can add another segment'}
                                </span>
                            );
                        })()}
                    </div>
                </div>
            </GlassCard>

            <SmsOptOutsPanel />

            <GlassModal
                isOpen={confirmTestOpen}
                onClose={() => setConfirmTestOpen(false)}
                title="Send a real test SMS?"
                footer={
                    <>
                        <GlassButton variant="secondary" onClick={() => setConfirmTestOpen(false)}>Cancel</GlassButton>
                        <GlassButton variant="danger" onClick={handleTest}>Confirm</GlassButton>
                    </>
                }
            >
                <p>This sends a real SMS and will be charged by your provider.</p>
            </GlassModal>

            <GlassModal
                isOpen={confirmRemoveOpen}
                onClose={() => setConfirmRemoveOpen(false)}
                title="Remove SMS configuration?"
                footer={
                    <>
                        <GlassButton variant="secondary" onClick={() => setConfirmRemoveOpen(false)}>Cancel</GlassButton>
                        <GlassButton variant="danger" onClick={handleRemove}>Confirm</GlassButton>
                    </>
                }
            >
                <p>Sends will fall back to the platform default account, if configured.</p>
            </GlassModal>
        </div>
    );
}
